import type { EkycLivenessResult, EkycOcrResult, EkycStatus } from '../../api/user/ekyc.api';

/**
 * Logic thuần của màn chụp eKYC, tách khỏi component để test được.
 * Mỗi lần gọi đọc giấy tờ hay kiểm tra khuôn mặt đều tốn phí nhà cung cấp, nên chỉ gọi lại khi thật cần.
 */

/** Bộ ảnh giấy tờ đã đọc thành công gần nhất (so theo đúng đối tượng Blob đã gửi). */
export interface DocumentImages {
  front: Blob;
  back: Blob | null;
}

/**
 * Có cần gọi lại bước đọc giấy tờ không. Chỉ bỏ qua khi giấy tờ đã đọc ra họ tên và ảnh mặt trước, mặt sau
 * vẫn là đúng bộ ảnh đã đọc lần trước (người dùng chỉ chụp lại khuôn mặt).
 */
export const needsDocumentRead = (
  lastRead: DocumentImages | null,
  ocr: EkycOcrResult | null,
  front: Blob,
  back: Blob | null
): boolean => {
  if (!ocr?.name || !lastRead) return true;
  return lastRead.front !== front || lastRead.back !== (back ?? null);
};

/** Bước khuôn mặt đạt khi máy chủ báo cả hai cờ đều đúng. */
export const isLivenessVerified = (live: EkycLivenessResult | null | undefined): boolean =>
  !!live && live.livenessPassed === true && live.isVerified === true;

/**
 * Được gọi onVerified khi nào: bước khuôn mặt đã đạt, đã có họ tên từ giấy tờ, và với luồng rút tiền
 * thì phải nhập đủ tài khoản ngân hàng. Trả về câu báo lỗi nếu chưa được, null nếu được.
 */
export const finishBlocker = (args: {
  faceVerified: boolean;
  ocr: EkycOcrResult | null;
  identityOnly?: boolean;
  bank: { bankName: string; bankAccountNumber: string; bankAccountName: string };
}): string | null => {
  const { faceVerified, ocr, identityOnly, bank } = args;
  if (!ocr?.name) return 'Cab chưa đọc được họ tên trên giấy tờ. Bạn hãy chụp lại giấy tờ.';
  if (!faceVerified) return 'Bạn cần hoàn tất bước xác minh khuôn mặt trước.';
  if (!identityOnly && (!bank.bankName || !bank.bankAccountNumber || !bank.bankAccountName)) {
    return 'Bạn hãy nhập đủ thông tin tài khoản ngân hàng.';
  }
  return null;
};

/** Lấy câu lỗi máy chủ trả: lỗi 400 để câu ở `detail`, một số chỗ cũ dùng `message`. */
export const ekycErrorMessage = (e: any, fallback: string): string => {
  const d = e?.response?.data;
  if (typeof d === 'string' && d.trim()) return d.trim();
  const msg = d?.detail || d?.message;
  return typeof msg === 'string' && msg.trim() ? msg.trim() : fallback;
};

/** Máy chủ báo phải chụp lại giấy tờ trước khi kiểm tra khuôn mặt. */
export const isDocumentRetakeError = (msg: string): boolean =>
  /chụp lại giấy tờ trước khi xác minh khuôn mặt/i.test(msg);

/**
 * Hết lượt thử vĩnh viễn (phải nhắn hỗ trợ) chỉ dựa vào cờ `locked` của GET Ekyc/status.
 * Không đoán từ câu lỗi: giới hạn số lần trong ngày cũng có chữ "quá số lần" nhưng chỉ là tạm thời.
 */
export const isEkycLocked = (status: EkycStatus | null | undefined): boolean => status?.locked === true;

/** Câu báo số lần thử còn lại, chỉ hiện khi người dùng đã hỏng ít nhất một lần và còn lượt. */
export const attemptsLeftNotice = (status: EkycStatus | null | undefined): string | null => {
  if (!status || status.locked) return null;
  const max = status.maxFailedAttempts ?? 0;
  const left = status.attemptsLeft;
  if (max <= 0 || left === null || left === undefined) return null;
  if (left >= max || left <= 0) return null;
  return `Bạn còn ${left} lần thử.`;
};
